import type { AppProps } from 'next/app';
import { QueryClient, QueryClientProvider, Hydrate } from 'react-query';
import * as Sentry from '@sentry/nextjs';
import Layout from '@component/layout';
import '../styles/reset.css';
import '../styles/globals.less';
import { injectFetch, injectSSRLocals, isNode } from '@tyc-pc/core';
import ErrorBoundary from '@tyc-pc/error-boundary';
import config from 'utils/config';
import logger from 'utils/logger';
import reportSentry from 'utils/reportSentry';
import captureWhiteScreenException from 'utils/captureWhiteScreenException';
import { SearchProvider } from '@component/hooks/search/searchParams';
import { UnAuthError } from 'utils/errors';

if (isNode) {
  const axios = require('axios');

  // Init CAT
  try {
    const { axiosUseCat } = require('../scripts/cat');
    axiosUseCat && axiosUseCat(axios);
    logger.info('[@tyc/cat] axiosUseCat');
  } catch (err) {
    logger.error('[@tyc/cat] axiosUseCat failed', err);
  }

  // 适配网关 API 风控
  axios.interceptors.response.use((response: any) => {
    const { config: axiosConfig, data } = response || {};
    const { ctx, url } = axiosConfig || {};
    const { res, resolvedUrl } = ctx || {};
    if (
      data?.errorCode?.toString() === '302004' &&
      ctx &&
      res &&
      !(res.headersSent || res.writableEnded)
    ) {
      const err = new UnAuthError(`[antiRisk] ${data.message || data.special}`, 429);
      err.path = url;
      logger.ctx(ctx).error(err, data);
      res.writeHead(302, {
        Location: `/login?from=${encodeURIComponent(resolvedUrl)}`,
      });
      res.end();
    }
    return response;
  }, undefined);
}

const { fetchConfig } = config;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: false,
    },
  },
});

const handleError = (err: any, info: any) => {
  try {
    // eslint-disable-next-line no-param-reassign
    err.message = `🔥 ${err.message} (Catched by ErrorBoundary)`;
  } catch (e) {
    // do nothing
  }
  logger.error(err, info);
  info &&
    Sentry.addBreadcrumb({
      category: 'info',
      message: 'componentDidCatch.info',
      data: info,
      level: Sentry.Severity.Fatal,
    });
  reportSentry(err, { level: 'fatal' });
};

captureWhiteScreenException();

function MyApp({ Component, pageProps }: AppProps) {
  injectFetch({
    originCapi: fetchConfig.capiServer,
    originNode: fetchConfig.napiServer,
    originNapi: fetchConfig.napiServer,
    originGapi: fetchConfig.gapiServer,
  });
  injectSSRLocals(config);
  return (
    <ErrorBoundary onCatch={handleError}>
      <QueryClientProvider client={queryClient}>
        <Hydrate state={pageProps.dehydratedState}>
          <SearchProvider>
            <Layout>
              {/* @ts-ignore TODO: component 因更新 core 等组件报错 */}
              <Component {...pageProps} />
            </Layout>
          </SearchProvider>
        </Hydrate>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default MyApp;
